import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { forwardRef, useRef, useState } from 'react'
import { Link, useParams } from 'react-router'
import { api, ApiError, type Category,
  type CategoryDisplayMode, type SpaceDetail } from '../../lib/api'
import { CategoryMark } from './CategoryMark'
import { TabBar } from '../../components/TabBar'
import { AddSheet } from '../items/AddSheet'
import { layout, maximumCategories, sousLaBarre } from '../../design/tokens'
import { copier } from '../../lib/liens'
import { deplacer, rangPourHauteur } from './ordreParGlissement'
import { BoutonActualiser } from '../../components/BoutonActualiser'

/**
 * Planche 06 du cahier des charges — page d'espace.
 *
 * « Deux niveaux visuels distincts : les catégories sont des blocs colorés (le contenu), les
 * modules des puces neutres (les outils). » En v1.0 il n'y a pas de modules : aucun n'est livré
 * avant le Lot 3, et le principe 6 les fait disparaître plutôt que de les griser. Le champ de
 * recherche et le bloc « à classer » suivent la même règle — ils arrivent au Lot 2.
 */
export function SpacePage() {
  const { spaceId = '' } = useParams()
  const [feuille, setFeuille] = useState(false)
  const [adresse, setAdresse] = useState(false)
  const [gerer, setGerer] = useState(false)

  const { data, isPending, isError } = useQuery<SpaceDetail>({
    queryKey: ['espace', spaceId],
    queryFn: () => api.space(spaceId),
    enabled: spaceId.length > 0,
  })

  return (
    <main
      className="mx-auto min-h-dvh bg-surface"
      style={{
        maxWidth: layout.column,
        paddingInline: layout.gutter,
        paddingBottom: sousLaBarre(32),
      }}
    >
      {isPending ? (
        <Squelette />
      ) : isError || !data ? (
        <p className="pt-8 text-body text-ink-muted">
          Cet espace est introuvable. Revenez à la liste de vos espaces.
        </p>
      ) : (
        <>
          <header className="flex items-center gap-3" style={{ height: layout.header }}>
            <CategoryMark initials={data.space.emblemInitials} hue={data.space.emblemHue} size="medium" />
            <h1 className="grow truncate font-serif text-screen">{data.space.name}</h1>
            {data.depositAddress ? (
              <button
                type="button"
                onClick={() => setAdresse(true)}
                aria-label="Adresse de dépôt par courrier"
                title="Envoyer par courrier"
                className="flex shrink-0 items-center justify-center rounded-field border border-border bg-card text-accent"
                style={{ width: 40, height: 40 }}
              >
                {/* Enveloppe dessinée en SVG : pas de fonte d'icônes à charger, et le trait
                    suit la couleur du texte. */}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="m2 7 10 6 10-6" />
                </svg>
              </button>
            ) : null}
            {/* Le bouton « changer d'espace » est retiré : la barre basse y mène depuis la
                décision L49, et deux chemins pour le même geste encombraient l'en-tête. */}
            <BoutonActualiser />
          </header>

          {/* Dit franchement, et au-dessus de tout : on est chez quelqu'un d'autre. Un accès
              d'administration silencieux ferait oublier où l'on est — et c'est précisément quand
              on l'oublie qu'on modifie ce qu'il ne fallait pas. */}
          {data.viewerIsAdminGuest ? (
            <p
              className="mb-3 rounded-card border p-3 text-meta"
              style={{
                borderColor: 'var(--color-danger)',
                color: 'var(--color-danger)',
              }}
            >
              Vous n'êtes pas membre de cet espace : vous le voyez parce que vous
              l'administrez. Tout ce que vous y faites y reste.
            </p>
          ) : null}

          {/* Les outils ont leur propre écran depuis la décision L50 : cette page portait les
              deux familles plus la gestion des catégories, et s'allongeait à chaque module
              ajouté. La barre basse mène désormais directement à l'une ou à l'autre. */}

          {/* « Gérer les catégories » descend sous la grille : on ouvre cet écran vingt fois
              pour consulter, une fois pour régler. Le mettre au-dessus du contenu donnait à un
              réglage rare la place d'une action quotidienne (décision L42). */}
          <div className="mb-4">
            <Link to={`/espace/${spaceId}/recents`} className="text-meta font-semibold text-accent">
              Voir toutes les idées
            </Link>
          </div>

          <ul className="grid grid-cols-2" style={{ gap: layout.gridGap }}>
            {data.categories.map((category) => (
              <CategoryBlock key={category.id} category={category} spaceId={spaceId} />
            ))}
          </ul>

          <button
            type="button"
            onClick={() => setGerer((ouvert) => !ouvert)}
            className="mt-6 w-full rounded-button border border-border bg-card text-card-title font-semibold"
            style={{ height: layout.primaryButtonHeight }}
          >
            {gerer ? 'Terminer' : 'Gérer les catégories'}
          </button>

          {gerer ? (
            <div className="mt-4">
              <NommerLEspace spaceId={spaceId} nom={data.space.name} />
              <GererCategories
                spaceId={spaceId}
                categories={data.categories}
                archivees={data.archivedCategories}
              />

              {/* La corbeille est ici et non dans les outils : on l'ouvre le jour où l'on a
                  supprimé quelque chose par erreur, pas tous les jours. */}
              <Link
                to={`/espace/${spaceId}/corbeille`}
                className="mt-4 flex w-full items-center justify-between rounded-field border border-border bg-card p-3 text-body"
                style={{ textDecoration: 'none' }}
              >
                Corbeille
                <span className="text-ink-muted">›</span>
              </Link>
            </div>
          ) : null}


          {data.categories.length === 0 ? (
            <p className="text-body text-ink-muted">
              Aucune catégorie ici pour l'instant. Ajoutez une idée, elle se rangera.
            </p>
          ) : null}
        </>
      )}

      <TabBar onAdd={() => setFeuille(true)} activeSpaceId={spaceId} />

      {feuille ? <AddSheet spaceId={spaceId} onClose={() => setFeuille(false)} /> : null}

      {adresse && data?.depositAddress ? (
        <FeuilleAdresse
          adresse={data.depositAddress}
          espace={data.space.name}
          onClose={() => setAdresse(false)}
        />
      ) : null}
    </main>
  )
}

/**
 * Bloc de catégorie : aplat de la teinte, marque à deux lettres, nom, compteur.
 * La marque accompagne toujours la couleur — la couleur ne porte jamais seule l'information.
 */
/**
 * Gestion des catégories — planche 06 : « 6 teintes, création libre ».
 *
 * La teinte n'est jamais choisie : le serveur attribue la suivante libre, ce qui garantit que
 * deux catégories ne se ressemblent pas. Archiver libère la teinte et renvoie les fiches
 * concernées « à classer » — rien n'est supprimé.
 */
/**
 * L'adresse à laquelle écrire pour déposer une fiche dans cet espace.
 *
 * Vit ici et non dans l'administration : ce sont les membres qui écrivent à cette adresse, et
 * ils n'y ont pas accès. Sans cet écran, il fallait la leur dicter de vive voix.
 */
function FeuilleAdresse({
  adresse,
  espace,
  onClose,
}: {
  adresse: string
  espace: string
  onClose: () => void
}) {
  const [copie, setCopie] = useState<'oui' | 'non' | null>(null)

  return (
    <div className="fixed inset-0 z-10 flex items-end justify-center" style={{ background: 'rgb(26 29 27 / 0.35)' }}>
      <section
        className="w-full rounded-t-sheet bg-card shadow-sheet"
        style={{
          maxWidth: layout.column,
          padding: layout.gutter,
          paddingBottom: 32,
          animation: 'monter 260ms ease-out',
        }}
      >
        <h2 className="font-serif text-record">Envoyer par courrier</h2>
        <p className="mt-2 text-body text-ink-muted">
          Un message envoyé à cette adresse devient une fiche dans « {espace} ». L'objet fait le
          titre, le texte fait la note. Un message qui ne contient qu'un lien devient une fiche
          lien.
        </p>

        {/* `select-all` : un appui long sélectionne l'adresse entière, ce qui reste le seul
            moyen de la copier tant que le presse-papier est indisponible. */}
        <p
          className="mt-4 select-all rounded-field bg-sunken p-4 text-card-title font-semibold"
          style={{ wordBreak: 'break-all' }}
        >
          {adresse}
        </p>

        <a
          href={`mailto:${encodeURIComponent(adresse)}`}
          className="mt-4 flex w-full items-center justify-center rounded-button bg-accent text-card-title font-semibold text-card"
          style={{ height: layout.primaryButtonHeight }}
        >
          Écrire un message
        </a>

        {/* Le bouton était caché tant que le site restait servi en clair, `navigator.clipboard`
            n'existant qu'en contexte sécurisé. Il s'affiche maintenant toujours : la copie passe
            par un repli qui, lui, fonctionne partout. */}
        <button
          type="button"
          onClick={async () => setCopie((await copier(adresse)) ? 'oui' : 'non')}
          className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold"
          style={{ height: layout.primaryButtonHeight }}
        >
          {copie === 'oui'
            ? 'Adresse copiée'
            : copie === 'non'
              ? 'Copie impossible — appui long sur l’adresse'
              : "Copier l'adresse"}
        </button>

        <button
          type="button"
          onClick={onClose}
          className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold"
          style={{ height: layout.primaryButtonHeight }}
        >
          Fermer
        </button>

        <p className="mt-4 text-meta text-ink-muted">
          Seuls les membres de cet espace peuvent y déposer une fiche : un message venu d'ailleurs
          est écarté, jamais rangé.
        </p>
      </section>
    </div>
  )
}


/**
 * Le nom de l'espace, modifiable par ses membres.
 *
 * Il ne l'était que depuis l'administration : un espace mal nommé obligeait à demander à
 * quelqu'un d'autre, pour un geste qui n'engage que ceux qui le lisent.
 */
function NommerLEspace({ spaceId, nom }: { spaceId: string; nom: string }) {
  const queryClient = useQueryClient()
  const [propose, setPropose] = useState(nom)
  const [erreur, setErreur] = useState<string | null>(null)

  const renommer = useMutation({
    mutationFn: () => api.renameSpace(spaceId, propose.trim()),
    onSuccess: async () => {
      setErreur(null)
      await queryClient.invalidateQueries({ queryKey: ['espace', spaceId] })
      await queryClient.invalidateQueries({ queryKey: ['espaces'] })
    },
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : 'Le changement a échoué.'),
  })

  return (
    <section className="mb-4">
      <label
        className="block text-label font-semibold uppercase text-ink-muted"
        htmlFor="nom-espace"
      >
        Nom de l'espace
      </label>
      <div className="mt-2 flex gap-2">
        <input
          id="nom-espace"
          value={propose}
          onChange={(evenement) => {
            setPropose(evenement.target.value)
            setErreur(null)
          }}
          maxLength={80}
          className="grow rounded-field border border-border bg-card px-4 outline-none"
          style={{ height: 48, fontSize: 16 }}
        />
        <button
          type="button"
          disabled={propose.trim().length === 0 || propose.trim() === nom || renommer.isPending}
          onClick={() => renommer.mutate()}
          className="shrink-0 rounded-button bg-accent px-4 text-meta font-semibold text-card disabled:opacity-40"
          style={{ height: 48 }}
        >
          Renommer
        </button>
      </div>
      {erreur ? <p className="mt-2 text-meta text-danger">{erreur}</p> : null}
    </section>
  )
}

function GererCategories({
  spaceId,
  categories,
  archivees,
}: {
  spaceId: string
  categories: readonly Category[]
  archivees: readonly Category[]
}) {
  const [nouveau, setNouveau] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const queryClient = useQueryClient()

  // L'ordre affiché pendant un glissement. Null : celui du serveur fait foi. Garder les deux
  // permet de suivre le doigt sans attendre le réseau, et de revenir en arrière s'il refuse.
  const [ordre, setOrdre] = useState<readonly Category[] | null>(null)
  const [saisie, setSaisie] = useState<string | null>(null)
  const lignes = useRef(new Map<string, HTMLLIElement>())

  const affichees = ordre ?? categories

  const reordonner = useMutation({
    mutationFn: (ids: readonly string[]) => api.reorderCategories(spaceId, ids),
    onSuccess: async () => {
      setOrdre(null)
      await rafraichir()
    },
    onError: (cause: unknown) => {
      // L'ordre local tombe : l'écran revient à ce que le serveur sait, plutôt que d'afficher un
      // rangement qui n'existe nulle part.
      setOrdre(null)
      echoue(cause)
    },
  })

  /**
   * Suit le doigt : à chaque mouvement, la ligne saisie prend le rang de la hauteur survolée.
   *
   * Les milieux sont relus à chaque mouvement plutôt que mémorisés au départ : la liste se
   * réorganise sous le doigt, et des positions figées désigneraient bientôt les mauvais rangs.
   */
  const glisser = (identifiant: string, hauteur: number) => {
    const courant = ordre ?? categories
    const depuis = courant.findIndex((category) => category.id === identifiant)

    if (depuis === -1) {
      return
    }

    const milieux = courant
      .filter((category) => category.id !== identifiant)
      .map((category) => {
        const element = lignes.current.get(category.id)
        const boite = element?.getBoundingClientRect()

        return boite ? boite.top + boite.height / 2 : Number.POSITIVE_INFINITY
      })

    const vers = rangPourHauteur(milieux, hauteur)

    if (vers !== depuis) {
      setOrdre(deplacer(courant, depuis, vers))
    }
  }

  const rafraichir = async () => {
    await queryClient.invalidateQueries({ queryKey: ['espace', spaceId] })
    await queryClient.invalidateQueries({ queryKey: ['fiches', spaceId] })
    await queryClient.invalidateQueries({ queryKey: ['aujourdhui'] })
  }

  const echoue = (cause: unknown) =>
    setErreur(cause instanceof ApiError ? cause.message : "L'opération a échoué.")

  const creer = useMutation({
    mutationFn: () => api.createCategory(spaceId, nouveau.trim()),
    onSuccess: async () => {
      setNouveau('')
      await rafraichir()
    },
    onError: echoue,
  })

  return (
    <section className="mb-4">
      <p className="mb-2 text-meta text-ink-muted">
        Glissez la poignée pour changer l'ordre — c'est celui de l'écran d'accueil.
      </p>

      <ul className="flex flex-col gap-2">
        {affichees.map((category, rang) => (
          <LigneCategorie
            key={category.id}
            spaceId={spaceId}
            category={category}
            saisie={saisie === category.id}
            onError={echoue}
            onDone={rafraichir}
            ref={(element) => {
              if (element) {
                lignes.current.set(category.id, element)
              } else {
                lignes.current.delete(category.id)
              }
            }}
            onPrise={() => setSaisie(category.id)}
            onGlisse={(hauteur) => glisser(category.id, hauteur)}
            onLache={() => {
              setSaisie(null)

              const ids = (ordre ?? categories).map((autre) => autre.id)
              const inchange = ids.every((id, index) => categories[index]?.id === id)

              if (inchange) {
                setOrdre(null)
              } else {
                reordonner.mutate(ids)
              }
            }}
            onDeplacerAuClavier={(pas) => {
              const courant = ordre ?? categories
              const vers = rang + pas

              if (vers < 0 || vers >= courant.length) {
                return
              }

              const suivant = deplacer(courant, rang, vers)
              setOrdre(suivant)
              reordonner.mutate(suivant.map((autre) => autre.id))
            }}
          />
        ))}
      </ul>

      {categories.length < maximumCategories ? (
        <div className="mt-4 flex flex-wrap gap-3">
          <input
            value={nouveau}
            onChange={(event) => {
              setNouveau(event.target.value)
              setErreur(null)
            }}
            placeholder="Nouvelle catégorie"
            className="grow rounded-field border border-border bg-card px-4 outline-none"
            style={{ height: 48, fontSize: 16, minWidth: 160 }}
          />
          <button
            type="button"
            disabled={nouveau.trim().length === 0 || creer.isPending}
            onClick={() => creer.mutate()}
            className="rounded-button bg-accent px-4 text-card-title font-semibold text-card disabled:opacity-40"
            style={{ height: 48 }}
          >
            Ajouter
          </button>
        </div>
      ) : (
        <p className="mt-4 text-meta text-ink-muted">
          {maximumCategories} catégories au maximum. Archivez-en une pour en créer une autre.
        </p>
      )}

      {/* Archiver est présenté comme réversible : le chemin du retour doit exister quelque part,
          et c'est ici. Restaurer échoue si la teinte a été reprise entre-temps — le message le
          dit alors, plutôt que de créer deux catégories de même couleur. */}
      {archivees.length > 0 ? (
        <div className="mt-6">
          <p className="text-label font-semibold uppercase text-ink-muted">Archivées</p>
          <ul className="mt-3 flex flex-col gap-3">
            {archivees.map((category) => (
              <LigneArchivee
                key={category.id}
                spaceId={spaceId}
                category={category}
                onError={echoue}
                onDone={rafraichir}
              />
            ))}
          </ul>
        </div>
      ) : null}

      {erreur ? (
        <p className="mt-3 text-meta" style={{ color: 'var(--color-danger)' }}>
          {erreur}
        </p>
      ) : null}
    </section>
  )
}

function LigneArchivee({
  spaceId,
  category,
  onError,
  onDone,
}: {
  spaceId: string
  category: Category
  onError: (cause: unknown) => void
  onDone: () => Promise<void>
}) {
  const [confirme, setConfirme] = useState(false)

  const restaurer = useMutation({
    mutationFn: () => api.patchCategory(spaceId, category.id, { archived: false }),
    onSuccess: onDone,
    onError,
  })

  const supprimer = useMutation({
    mutationFn: () => api.deleteCategory(spaceId, category.id),
    onSuccess: onDone,
    onError,
  })

  return (
    <li className="flex flex-wrap items-center gap-3" style={{ opacity: 0.72 }}>
      <CategoryMark initials={category.initials} hue={category.hue} size="small" />
      <span className="grow text-body">{category.name}</span>

      {confirme ? (
        <>
          <button
            type="button"
            onClick={() => supprimer.mutate()}
            className="text-meta font-semibold"
            style={{ color: 'var(--color-danger)' }}
          >
            Confirmer
          </button>
          <button
            type="button"
            onClick={() => setConfirme(false)}
            className="text-meta font-semibold text-ink-muted"
          >
            Annuler
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => restaurer.mutate()}
            className="text-meta font-semibold text-accent"
          >
            Restaurer
          </button>
          {/* Une catégorie archivée ne porte plus de fiches — elles sont retombées « à classer »
              — donc l'effacer ne fait rien perdre. C'est pour ça que la suppression n'est
              proposée qu'ici, et jamais sur une catégorie vivante. */}
          <button
            type="button"
            onClick={() => setConfirme(true)}
            className="text-meta font-semibold text-ink-muted"
          >
            Supprimer
          </button>
        </>
      )}
    </li>
  )
}

/**
 * Une catégorie dans la liste de gestion : une rangée qui se lit, et une feuille pour la régler.
 *
 * Tout tenait auparavant sur une seule rangée — nom, mode d'affichage, deux cases, archivage —
 * qui débordait sur trois lignes et se lisait mal. Une catégorie se règle rarement et mérite
 * qu'on lui donne de la place quand on s'en occupe (correction S63).
 */
const LigneCategorie = forwardRef<
  HTMLLIElement,
  {
    spaceId: string
    category: Category
    saisie: boolean
    onError: (cause: unknown) => void
    onDone: () => Promise<void>
    onPrise: () => void
    onGlisse: (hauteur: number) => void
    onLache: () => void
    onDeplacerAuClavier: (pas: number) => void
  }
>(function LigneCategorie(
  { spaceId, category, saisie, onError, onDone, onPrise, onGlisse, onLache, onDeplacerAuClavier },
  ref,
) {
  const [ouverte, setOuverte] = useState(false)

  return (
    <li
      ref={ref}
      className="flex items-stretch gap-2"
      style={{
        // La ligne saisie se détache, sans quoi on ne sait plus laquelle on tient.
        opacity: saisie ? 0.9 : 1,
        transform: saisie ? 'scale(1.01)' : undefined,
      }}
    >
      {/* La poignée est un bouton à part, et c'est tout l'enjeu : glisser la ligne entière
          ouvrirait la fiche au moindre appui, et empêcherait la page de défiler au doigt
          partout ailleurs. `touch-action: none` ne désarme le défilement que sur ces 44 px. */}
      <button
        type="button"
        aria-label={`Déplacer ${category.name}`}
        onPointerDown={(evenement) => {
          evenement.currentTarget.setPointerCapture(evenement.pointerId)
          onPrise()
        }}
        onPointerMove={(evenement) => {
          if (saisie) {
            onGlisse(evenement.clientY)
          }
        }}
        onPointerUp={() => onLache()}
        onPointerCancel={() => onLache()}
        onKeyDown={(evenement) => {
          // Au clavier aussi : un glissement ne se fait pas sans pointeur, et l'ordre doit
          // rester atteignable (écart E9).
          if (evenement.key === 'ArrowUp' || evenement.key === 'ArrowDown') {
            evenement.preventDefault()
            onDeplacerAuClavier(evenement.key === 'ArrowUp' ? -1 : 1)
          }
        }}
        className="flex shrink-0 items-center justify-center rounded-card border border-border bg-card text-ink-muted"
        style={{ width: 44, touchAction: 'none', cursor: 'grab' }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
          <circle cx="6" cy="4" r="1.4" />
          <circle cx="10" cy="4" r="1.4" />
          <circle cx="6" cy="8" r="1.4" />
          <circle cx="10" cy="8" r="1.4" />
          <circle cx="6" cy="12" r="1.4" />
          <circle cx="10" cy="12" r="1.4" />
        </svg>
      </button>

      <button
        type="button"
        onClick={() => setOuverte(true)}
        className="flex grow items-center gap-3 rounded-card border border-border bg-card p-3 text-left"
        style={{ minHeight: 56 }}
      >
        <CategoryMark initials={category.initials} hue={category.hue} size="small" />
        <span className="grow truncate">
          <span className="block truncate text-card-title font-semibold">{category.name}</span>
          <span className="block truncate text-meta text-ink-muted">
            {LIBELLES_AFFICHAGE[category.displayMode]}
            {category.showIngredients ? ' · ingrédients' : ''}
            {category.showGift ? ' · cadeau' : ''}
            {category.showExpense ? ' · dépense' : ''}
          </span>
        </span>
        <span aria-hidden="true" className="shrink-0 text-ink-muted">
          ›
        </span>
      </button>

      {ouverte ? (
        <FeuilleCategorie
          spaceId={spaceId}
          category={category}
          onError={onError}
          onDone={onDone}
          onClose={() => setOuverte(false)}
        />
      ) : null}
    </li>
  )
})

const LIBELLES_AFFICHAGE: Record<CategoryDisplayMode, string> = {
  cards: 'Cartes',
  list: 'Liste',
  checklist: 'Liste à cocher',
}

/** Tout ce qui se règle sur une catégorie, avec la place de l'expliquer. */
function FeuilleCategorie({
  spaceId,
  category,
  onError,
  onDone,
  onClose,
}: {
  spaceId: string
  category: Category
  onError: (cause: unknown) => void
  onDone: () => Promise<void>
  onClose: () => void
}) {
  const [nom, setNom] = useState(category.name)

  const corriger = useMutation({
    mutationFn: (patch: Parameters<typeof api.patchCategory>[2]) =>
      api.patchCategory(spaceId, category.id, patch),
    onSuccess: onDone,
    onError,
  })

  const archiver = useMutation({
    mutationFn: () => api.patchCategory(spaceId, category.id, { archived: true }),
    onSuccess: async () => {
      await onDone()
      onClose()
    },
    onError,
  })

  return (
    <div
      className="fixed inset-0 z-10 flex items-end justify-center"
      style={{ background: 'rgb(26 29 27 / 0.35)' }}
    >
      <section
        className="w-full overflow-y-auto rounded-t-sheet bg-card shadow-sheet"
        style={{
          maxWidth: layout.column,
          maxHeight: '86dvh',
          padding: layout.gutter,
          paddingBottom: 32,
          animation: 'monter 260ms ease-out',
        }}
      >
        <div className="flex items-center gap-3">
          <CategoryMark initials={category.initials} hue={category.hue} size="medium" />
          <h2 className="grow font-serif text-record">{category.name}</h2>
        </div>

        <label className="mt-4 block text-label font-semibold uppercase text-ink-muted" htmlFor="nom-categorie">
          Nom
        </label>
        <div className="mt-2 flex gap-2">
          <input
            id="nom-categorie"
            value={nom}
            onChange={(evenement) => setNom(evenement.target.value)}
            className="grow rounded-field border border-border bg-card px-4 outline-none"
            style={{ height: 48, fontSize: 16 }}
          />
          <button
            type="button"
            disabled={nom.trim().length === 0 || nom.trim() === category.name}
            onClick={() => corriger.mutate({ name: nom.trim() })}
            className="rounded-button bg-accent px-4 text-meta font-semibold text-card disabled:opacity-40"
            style={{ height: 48 }}
          >
            Renommer
          </button>
        </div>

        <p className="mt-6 text-label font-semibold uppercase text-ink-muted">Affichage des fiches</p>
        <ul className="mt-2 flex flex-col gap-2">
          {(Object.keys(LIBELLES_AFFICHAGE) as CategoryDisplayMode[]).map((mode) => (
            <li key={mode}>
              <button
                type="button"
                onClick={() => corriger.mutate({ displayMode: mode })}
                aria-pressed={category.displayMode === mode}
                className="w-full rounded-button border text-card-title font-semibold"
                style={{
                  height: 48,
                  background:
                    category.displayMode === mode ? 'var(--color-accent)' : 'var(--color-card)',
                  color: category.displayMode === mode ? 'var(--color-card)' : 'var(--color-ink)',
                  borderColor:
                    category.displayMode === mode ? 'var(--color-accent)' : 'var(--color-border)',
                }}
              >
                {LIBELLES_AFFICHAGE[mode]}
              </button>
            </li>
          ))}
        </ul>

        <p className="mt-6 text-label font-semibold uppercase text-ink-muted">
          Ce que ses fiches savent faire
        </p>

        <label className="mt-3 flex items-start gap-3">
          <input
            type="checkbox"
            checked={category.showIngredients}
            onChange={(evenement) => corriger.mutate({ showIngredients: evenement.target.checked })}
            style={{ width: 22, height: 22, flexShrink: 0, marginTop: 2 }}
          />
          <span>
            <span className="block text-body">Extraire les ingrédients</span>
            <span className="block text-meta text-ink-muted">
              Un bouton lit la liste d'une recette et l'envoie dans les courses.
            </span>
          </span>
        </label>

        <label className="mt-3 flex items-start gap-3">
          <input
            type="checkbox"
            checked={category.showGift}
            onChange={(evenement) => corriger.mutate({ showGift: evenement.target.checked })}
            style={{ width: 22, height: 22, flexShrink: 0, marginTop: 2 }}
          />
          <span>
            <span className="block text-body">En faire un cadeau</span>
            <span className="block text-meta text-ink-muted">
              Un bouton transforme la fiche en idée de cadeau, cachée à son destinataire.
            </span>
          </span>
        </label>

        <label className="mt-3 flex items-start gap-3">
          <input
            type="checkbox"
            checked={category.showExpense}
            onChange={(evenement) => corriger.mutate({ showExpense: evenement.target.checked })}
            style={{ width: 22, height: 22, flexShrink: 0, marginTop: 2 }}
          />
          <span>
            <span className="block text-body">Noter une dépense</span>
            <span className="block text-meta text-ink-muted">
              Ce que la fiche a coûté rejoint les dépenses partagées. Sur un cadeau, le montant
              reste caché à son destinataire.
            </span>
          </span>
        </label>

        <button
          type="button"
          onClick={onClose}
          className="mt-6 w-full rounded-button bg-accent text-card-title font-semibold text-card"
          style={{ height: layout.primaryButtonHeight }}
        >
          Fermer
        </button>

        <button
          type="button"
          onClick={() => archiver.mutate()}
          className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold"
          style={{ height: layout.primaryButtonHeight }}
        >
          Archiver cette catégorie
        </button>
        <p className="mt-2 text-meta text-ink-muted">
          Ses fiches retombent « à classer » et la teinte redevient disponible. Réversible depuis
          la liste des archivées.
        </p>
      </section>
    </div>
  )
}

function CategoryBlock({ category, spaceId }: { category: Category; spaceId: string }) {
  return (
    <li>
      <Link
        to={`/espace/${spaceId}/recents?categorie=${category.id}`}
        className="block rounded-card p-4"
        style={{
          background: `var(--color-cat-${category.hue}-fill)`,
          minHeight: 104,
        }}
      >
        <CategoryMark initials={category.initials} hue={category.hue} size="small" />
        <p className="mt-3 text-card-title font-semibold">{category.name}</p>
        <p className="mt-1 text-meta" style={{ color: 'var(--color-ink)' }}>
          {category.itemCount}
        </p>
      </Link>
    </li>
  )
}

/**
 * « Le squelette reprend exactement la géométrie de la carte réelle. » Jamais de roue qui
 * tourne, jamais d'écran blanc (planche 19).
 */
function Squelette() {
  return (
    <>
      <div className="flex items-center gap-3" style={{ height: layout.header }}>
        <div className="rounded-field bg-sunken" style={{ width: 40, height: 40 }} />
        <div className="grow rounded-field bg-sunken" style={{ height: 34 }} />
      </div>
      <ul className="grid grid-cols-2" style={{ gap: layout.gridGap }}>
        {[0, 1, 2, 3, 4, 5].map((index) => (
          <li key={index} className="rounded-card bg-sunken" style={{ height: 104 }} />
        ))}
      </ul>
    </>
  )
}
